import React, { useState, useEffect, useRef } from 'react';
import Modal from '../UI/Modal';
import Alert from '../UI/Alert';
import LoadingIndicator from '../Common/LoadingIndicator';
import TemplateSelector from './TemplateSelector';
import DropdownMenu from '../UI/DropdownMenu';
import { useNotification } from '../../context/NotificationContext';
import { 
    Save, 
    X, 
    FileText, 
    CheckCircle2,
    Eye,
    EyeOff,
    Tag,
    Paperclip,
    ChevronDown,
    ClipboardList,
    AlertCircle,
    StickyNote
} from 'lucide-react';

const EditorNotas = ({ pacienteId, nota, onSave, onClose }) => {
    const { error: showError } = useNotification();
    const [formData, setFormData] = useState({
        tipo: 'evolucao',
        categoria: null,
        titulo: '',
        conteudo: '',
        template_id: null,
        tags: [],
        rascunho: true,
        observacoes: ''
    });
    const [showPreview, setShowPreview] = useState(false);
    const [showTemplateSelector, setShowTemplateSelector] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const textareaRef = useRef(null);

    useEffect(() => {
        if (nota) {
            setFormData({
                tipo: nota.tipo || 'evolucao',
                categoria: nota.categoria || null,
                titulo: nota.titulo || '',
                conteudo: nota.conteudo || '',
                template_id: nota.template_id || null,
                tags: nota.tags || [],
                rascunho: nota.rascunho !== undefined ? nota.rascunho : true,
                observacoes: nota.observacoes || ''
            });
        }
    }, [nota]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError(null);
    };

    const handleAddTag = () => {
        if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
            handleChange('tags', [...formData.tags, tagInput.trim()]);
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        handleChange('tags', formData.tags.filter(tag => tag !== tagToRemove));
    };

    const handleTemplateSelect = (template) => {
        if (template) {
            // Se o template tem ID numérico, é um template do banco. Caso contrário, é um template básico pré-definido
            const templateId = typeof template.id === 'number' ? template.id : null;
            
            setFormData(prev => ({
                ...prev,
                template_id: templateId,
                tipo: template.tipo,
                categoria: template.categoria || null,
                conteudo: template.conteudo_template || prev.conteudo
            }));
        }
        setShowTemplateSelector(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!formData.conteudo.trim()) {
            const errorMsg = 'O conteúdo da nota é obrigatório.';
            setError(errorMsg);
            showError(errorMsg);
            return;
        }

        setIsSaving(true);
        try {
            await onSave(formData);
        } catch (err) {
            const errorMsg = err?.response?.data?.message || err?.message || 'Erro ao salvar nota.';
            setError(errorMsg);
            showError(errorMsg);
        } finally {
            setIsSaving(false);
        }
    };

    const formatPreview = (text) => {
        // Converte quebras de linha em <br> e formata texto básico
        return text
            .split('\n')
            .map((line, i) => (
                <React.Fragment key={i}>
                    {line}
                    {i < text.split('\n').length - 1 && <br />}
                </React.Fragment>
            ));
    };

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={nota ? 'Editar Nota Clínica' : 'Nova Nota Clínica'}
            size="2xl"
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                {error && <Alert type="error" message={error} />}

                {/* Tipo e Categoria */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tipo <span className="text-red-500">*</span>
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-left"
                                >
                                    <span className="text-gray-700">
                                        {formData.tipo === 'evolucao' ? 'Evolução' : 
                                         formData.tipo === 'intercorrencia' ? 'Intercorrência' : 
                                         formData.tipo === 'observacao' ? 'Observação' : 'Selecione...'}
                                    </span>
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                </button>
                            }
                            items={[
                                {
                                    id: 'evolucao',
                                    label: 'Evolução',
                                    icon: ClipboardList,
                                    iconColor: 'text-emerald-600',
                                    onClick: () => handleChange('tipo', 'evolucao')
                                },
                                {
                                    id: 'intercorrencia',
                                    label: 'Intercorrência',
                                    icon: AlertCircle,
                                    iconColor: 'text-orange-600',
                                    onClick: () => handleChange('tipo', 'intercorrencia')
                                },
                                {
                                    id: 'observacao',
                                    label: 'Observação',
                                    icon: StickyNote,
                                    iconColor: 'text-blue-600',
                                    onClick: () => handleChange('tipo', 'observacao')
                                }
                            ]}
                            position="bottom-left"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Categoria
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-left"
                                >
                                    <span className="text-gray-700">
                                        {formData.categoria === 'soap' ? 'SOAP' : 
                                         formData.categoria === 'subjetivo' ? 'Subjetivo' : 
                                         formData.categoria === 'objetivo' ? 'Objetivo' : 
                                         formData.categoria === 'avaliacao' ? 'Avaliação' : 
                                         formData.categoria === 'plano' ? 'Plano' : 'Selecione...'}
                                    </span>
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                </button>
                            }
                            items={[
                                {
                                    id: 'none',
                                    label: 'Selecione...',
                                    icon: X,
                                    iconColor: 'text-gray-400',
                                    onClick: () => handleChange('categoria', null)
                                },
                                {
                                    id: 'soap',
                                    label: 'SOAP',
                                    icon: FileText,
                                    iconColor: 'text-emerald-600',
                                    onClick: () => handleChange('categoria', 'soap')
                                },
                                {
                                    id: 'subjetivo',
                                    label: 'Subjetivo',
                                    icon: Eye,
                                    iconColor: 'text-blue-600',
                                    onClick: () => handleChange('categoria', 'subjetivo')
                                },
                                {
                                    id: 'objetivo',
                                    label: 'Objetivo',
                                    icon: CheckCircle2,
                                    iconColor: 'text-green-600',
                                    onClick: () => handleChange('categoria', 'objetivo')
                                },
                                {
                                    id: 'avaliacao',
                                    label: 'Avaliação',
                                    icon: ClipboardList,
                                    iconColor: 'text-purple-600',
                                    onClick: () => handleChange('categoria', 'avaliacao')
                                },
                                {
                                    id: 'plano',
                                    label: 'Plano',
                                    icon: FileText,
                                    iconColor: 'text-orange-600',
                                    onClick: () => handleChange('categoria', 'plano')
                                }
                            ]}
                            position="bottom-left"
                        />
                    </div>
                </div>

                {/* Título */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Título
                    </label>
                    <input
                        type="text"
                        value={formData.titulo}
                        onChange={(e) => handleChange('titulo', e.target.value)}
                        placeholder="Título da nota (opcional)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                </div>

                {/* Botões de ação do editor */}
                <div className="flex items-center justify-between border-b border-gray-200 pb-2">
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setShowTemplateSelector(true)}
                            className="flex items-center gap-1 px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                        >
                            <FileText className="w-4 h-4" />
                            Templates
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowPreview(!showPreview)}
                            className="flex items-center gap-1 px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                        >
                            {showPreview ? (
                                <>
                                    <EyeOff className="w-4 h-4" />
                                    Ocultar Preview
                                </>
                            ) : (
                                <>
                                    <Eye className="w-4 h-4" />
                                    Preview
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Editor de Conteúdo */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Conteúdo <span className="text-red-500">*</span>
                    </label>
                    {showPreview ? (
                        <div className="min-h-[300px] p-4 border border-gray-300 rounded-lg bg-gray-50">
                            <div className="prose max-w-none">
                                <h3 className="text-lg font-semibold mb-2">{formData.titulo || '(Sem título)'}</h3>
                                <div className="text-gray-700 whitespace-pre-wrap">
                                    {formatPreview(formData.conteudo || '')}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <textarea
                            ref={textareaRef}
                            value={formData.conteudo}
                            onChange={(e) => handleChange('conteudo', e.target.value)}
                            placeholder="Digite o conteúdo da nota clínica..."
                            className="w-full min-h-[300px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm"
                            required
                        />
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                        {formData.conteudo.length} caracteres
                    </p>
                </div>

                {/* Tags */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tags
                    </label>
                    <div className="flex gap-2 mb-2">
                        <input
                            type="text"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddTag();
                                }
                            }}
                            placeholder="Adicionar tag (Enter para adicionar)"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                        <button
                            type="button"
                            onClick={handleAddTag}
                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        >
                            <Tag className="w-4 h-4" />
                        </button>
                    </div>
                    {formData.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {formData.tags.map((tag, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs"
                                >
                                    {tag}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveTag(tag)}
                                        className="hover:text-emerald-900"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Observações */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Observações
                    </label>
                    <textarea
                        value={formData.observacoes}
                        onChange={(e) => handleChange('observacoes', e.target.value)}
                        placeholder="Observações adicionais (opcional)"
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                </div>

                {/* Opções */}
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={formData.rascunho}
                            onChange={(e) => handleChange('rascunho', e.target.checked)}
                            className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                        />
                        <span className="text-sm text-gray-700">Salvar como rascunho</span>
                    </label>
                </div>

                {/* Botões de ação */}
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                        disabled={isSaving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={isSaving || !formData.conteudo.trim()}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        {isSaving ? (
                            <>
                                <LoadingIndicator />
                                Salvando...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                {formData.rascunho ? 'Salvar Rascunho' : 'Salvar Nota'}
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Modal de Seleção de Template */}
            {showTemplateSelector && (
                <TemplateSelector
                    pacienteId={pacienteId}
                    tipo={formData.tipo}
                    onSelect={handleTemplateSelect}
                    onClose={() => setShowTemplateSelector(false)}
                />
            )}
        </Modal>
    );
};

export default EditorNotas;

